import React, { Fragment, useEffect, useState } from "react";
import { Checkbox, FormControlLabel, FormGroup } from "@mui/material";
import { Card, Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../components/Admin/Reusable/Breadcrumb";
import { createNewRole, fetchRoleById, updateRole } from "../actions/role";
import SubmitButtonForm from "../components/Admin/Reusable/Form/SubmitButtonForm";
import FormTitleMain from "../components/Admin/Reusable/FormTitleMain";
import InputField from "../components/Admin/Reusable/InputField";

import { useSelector } from "react-redux";
import { basicFormValidationHandler } from "../utils";
import { toast } from "react-toastify";
import { isEmpty } from "lodash";
import { fetchMenuFormList } from "../actions/menu";
import { roleActions } from "../reducers/role";
import GlobalProgressOverlay from "../components/Admin/Reusable/GlobalProgressOverlay";

const RoleAdd = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams();

  const [errors, setErrors] = useState({});
  const { roleDetail, loading, success, actionType } = useSelector((state) => state.roleReducer);
  const { menuFormList } = useSelector((state) => state.menuReducer);

  const initialState = {
    name: "",
    menus: "",
  };

  //osnovi podaci
  const [data, setData] = useState(initialState);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [progress, setProgress] = useState(0);
  const [showLoader, setShowLoader] = useState(false);

  //breadcrumbs
  const translateForm = "roleForm";
  const translateModule = "role";
  const pageType = id ? "Edit" : "Add";
  const breadcrumbItems = [
    { label: t(`breadcrumb.${translateModule}`) },
    {
      label: t(`breadcrumb.${translateModule}${pageType}`),
      active: true,
    },
  ];

  const handleCheckboxChange = (event, option) => {
    if (event.target.checked) {
      setSelectedOptions([...selectedOptions, option]);
    } else {
      setSelectedOptions(selectedOptions.filter((item) => item !== option));
    }
  };

  useEffect(() => {
    dispatch(fetchMenuFormList());
  }, [dispatch]);

  useEffect(() => {
    if (!id) {
      // Ako `id` ne postoji, očisti stanje za novu kreaciju
      setData(initialState);
    } else {
      // Ako `id` postoji, učitaj podatke za korisnika
      dispatch(fetchRoleById(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (roleDetail && id) {
      setData({
        ...roleDetail,
      });
      setSelectedOptions(roleDetail?.["menus"]?.map((el) => el.id) || []);
    }
  }, [roleDetail, id, dispatch]);

  useEffect(() => {
    let interval;
    let timeout;

    if (loading) {
      setShowLoader(true);
      setProgress(0);

      interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            return 100;
          }
          return prev + 5;
        });
      }, 100);
    } else if (!loading && showLoader) {
      setProgress(100);
      timeout = setTimeout(() => {
        setShowLoader(false);
        setProgress(0);
      }, 2000);
    }

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [loading]);

    useEffect(() => {
      if (success) {
        const timeout = setTimeout(() => {
          navigate("/role-list");
  
          if (actionType === "create") {
            toast.success("Uspešno ste kreirali novu rolu", { theme: "colored" });
          } else if (actionType === "update") {
            toast.success("Uspešno ste izmenili rolu", { theme: "colored" });
          }
  
          dispatch(roleActions.clearError());
        }, 2000);
  
        return () => clearTimeout(timeout);
      } else {
        dispatch(roleActions.clearError());
      }
    }, [success]); 

  const userValidationHandler = () => {
    const { name } = data;

    let errorsObject = {};
    errorsObject = basicFormValidationHandler({
      name,
    });

    setErrors(errorsObject);
    if (!isEmpty(errorsObject))
      toast.error("Morate popuniti sva obavezna polja!", {
        theme: "colored",
      });
    return !isEmpty(errorsObject);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (userValidationHandler()) {
      return;
    }

    if (id) {
      data.id = id;
    }

    const roleForSave = { ...data, menus: selectedOptions };

    if (id) {
      dispatch(updateRole(roleForSave, navigate, id));
    } else {
      dispatch(createNewRole(roleForSave, navigate));
    }
  };

  const handleInputChange = (e) => {
    const { name, type, value, checked } = e.target;

    setData({
      ...data,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  return (
    <Fragment>
      <Breadcrumb
        breadcrumbItems={breadcrumbItems}
        type={`${translateModule}${pageType}`}
        id={id}
        pageType={pageType}
      />
      {showLoader && <GlobalProgressOverlay progress={progress} />}
      <Container className="role-container">
        <Row>
          <Col lg={12}>
            <form onSubmit={handleSubmit} noValidate>
              <Row>
                <Col lg={12}>
                  <div className="text-end mb-2">
                    <SubmitButtonForm id={id} />
                  </div>
                </Col>
              </Row>
              <Row>
                <Col lg={9}>
                  <Row>
                    <Col lg={12}>
                      <Card>
                        <Card.Header>
                          <FormTitleMain />
                        </Card.Header>
                        <Card.Body>
                          <div className="blog-holder-admin-left-side-text">
                            <Row>
                              <Col lg={12} md={12} xs={12} className="mb-2">
                                <InputField
                                  label={t(`${translateForm}.name`)}
                                  name="name"
                                  value={data.name}
                                  onChange={handleInputChange}
                                  error={errors?.name}
                                  errorsHandler={setErrors}
                                  fullWidth
                                  size="small"
                                  required
                                />
                              </Col>
                            </Row>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Col>
                <Col lg={3}>
                  <Row>
                    <Col lg={12}>
                      <Card>
                        <Card.Header>Moduli</Card.Header>
                        <Card.Body>
                          <FormGroup>
                            {menuFormList?.map((option, index) => (
                              <FormControlLabel
                                key={index}
                                control={
                                  <Checkbox
                                    checked={selectedOptions.includes(
                                      option.id
                                    )}
                                    onChange={(e) =>
                                      handleCheckboxChange(e, option.id)
                                    }
                                  />
                                }
                                label={option.name}
                              />
                            ))}
                          </FormGroup>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </form>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default RoleAdd;
